import { computed } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, pipe, switchMap } from 'rxjs';

export type PagingInformation = {
  page?: number;
  maxElements?: number;
  maxPages?: number;
  itemsPerPage?: number;
};

export type ServerResponse<T> = {
  list: T[];
} & PagingInformation;

type PagedListState<T> = {
  _data: T[];
  _paging: PagingInformation;
  _loading: boolean;
  _loadingMore?: boolean;
  _serverCall?: (offset: number, batchSize: number) => Observable<ServerResponse<T>>;
  _page: number;
  _pageSize: number;
};

export type PagedListViewModel<T> = {
  data: T[];
  paging: PagingInformation;
  loading: boolean;
  loadingMore?: boolean;
  hasMore: boolean;
  pageSize: number;
};

const initialState = <T>(): PagedListState<T> => ({
  _data: [],
  _page: 1,
  _pageSize: 10,
  _serverCall: undefined,
  _loading: false,
  _loadingMore: false,
  _paging: {},
});

export type WithPagedListInitOptions<T> = {
  pageSize?: number;
  loadPageInitially?: boolean;
  serverCall?: (offset: number, batchSize: number) => Observable<ServerResponse<T>>;
  initialValues?: T[];
  initialLoadingState?: boolean;
};

export function withPagedList<T>() {
  return signalStoreFeature(
    withState(initialState<T>()),
    withComputed((store) => {
      const { _data, _loading, _loadingMore, _paging, _pageSize } = store;

      return {
        viewModel: computed<PagedListViewModel<T>>(() => {
          const paging = _paging();
          const pageSize = _pageSize();
          const hasMore =
            paging.maxElements !== undefined &&
            paging.page !== undefined &&
            paging.page >= 1 &&
            paging.page * pageSize < paging.maxElements;

          return {
            data: _data(),
            paging: paging,
            loading: _loading(),
            loadingMore: _loadingMore(),
            hasMore: hasMore,
            pageSize: pageSize,
          };
        }),
      };
    }),
    withMethods((store) => {
      const { _serverCall, _page, _pageSize } = store;

      const init = (options?: WithPagedListInitOptions<T>) => {
        patchState(store, {
          ...(options?.pageSize && { _pageSize: options.pageSize }),
          ...(options?.initialLoadingState !== undefined && { _loading: options.initialLoadingState }),
          _serverCall: options?.serverCall,
          _data: options?.initialValues ?? [],
        });

        if (options.loadPageInitially !== false) {
          loadPage(1, options?.pageSize);
        }
      };

      const reloadPage = () => {
        loadPage(_page(), _pageSize());
      };

      const loadPage = (page = 1, pageSize?: number) => {
        if (!_serverCall()) {
          console.debug('Server call not set');
          return;
        }

        patchState(store, {
          _loading: true,
          _page: page,
          _pageSize: pageSize ?? _pageSize(),
        });

        loadData('page');
      };

      const loadNextPage = () => {
        if (!_serverCall()) {
          console.debug('Server call not set');
          return;
        }

        patchState(store, {
          _loading: true,
          _loadingMore: true,
          _page: _page() + 1,
        });

        loadData('infinite');
      };

      const loadData = rxMethod<'page' | 'infinite' | undefined>(
        pipe(
          switchMap((mode = 'page') =>
            _serverCall()(_page(), _pageSize()).pipe(
              tapResponse(
                (response: ServerResponse<T>) => {
                  patchState(store, (state) => ({
                    _data: mode === 'infinite' ? [...state._data, ...response.list] : response.list,
                    _paging: {
                      page: response.page,
                      maxElements: response.maxElements,
                      maxPages: response.maxPages,
                      itemsPerPage: response.itemsPerPage,
                    },
                    _loading: false,
                    _loadingMore: false,
                  }));
                },
                (error) => {
                  patchState(store, {
                    _loading: false,
                    _loadingMore: false,
                  });
                  console.error('Error loading data', error);
                  return error;
                },
              ),
            ),
          ),
        ),
      );

      return {
        init,
        loadPage,
        loadNextPage,
        reloadPage,
      };
    }),
  );
}
